import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProofShield } from '@/components/profile/ProofShield';
import { formatUsdCents } from '@/lib/formatMoney';

interface ItemPinProps {
	itemId: string | null | undefined;
	title?: string | null;
	imageUrl?: string | null;
	subtitle?: string | null;
	priceCents?: number | null;
	hasProofPack?: boolean | null;
	/** Compact = sticky pin above messages; default = inline card-style. */
	variant?: 'pin' | 'card';
}

/**
 * Reusable item context surface — appears as a sticky "Now discussing" pin
 * above the messages, and as the body of an `item_card` inline message.
 */
export const ItemPin = ({
	itemId,
	title,
	imageUrl,
	subtitle,
	priceCents,
	hasProofPack,
	variant = 'pin',
}: ItemPinProps) => {
	if (!itemId) return null;
	const price =
		typeof priceCents === 'number' && priceCents > 0
			? `${formatUsdCents(priceCents)}`
			: null;

	const body = (
		<>
			<div className="relative h-10 w-10 rounded-md overflow-hidden bg-white/[0.05] shrink-0">
				{imageUrl ? (
					<img src={imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
				) : (
					<div className="h-full w-full grid place-items-center text-[10px] text-muted-foreground/60">
						—
					</div>
				)}
				{hasProofPack && (
					<div className="absolute -top-1 -right-1">
						<ProofShield size="sm" />
					</div>
				)}
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-[12.5px] text-foreground font-medium truncate leading-tight">
					{title || 'Item'}
				</div>
				<div className="text-[11px] text-muted-foreground truncate leading-tight">
					{subtitle || 'Tap to view'}
				</div>
			</div>
			{price && (
				<div className="text-[12.5px] font-semibold tabular-nums text-[hsl(var(--active-gold))] shrink-0">
					{price}
				</div>
			)}
			<ExternalLink className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
		</>
	);

	if (variant === 'pin') {
		return (
			<Link
				to={`/item/${itemId}`}
				className={cn(
					'flex items-center gap-2.5 px-3 py-2 rounded-lg',
					'bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.06] transition-colors'
				)}
			>
				{body}
			</Link>
		);
	}

	return (
		<Link
			to={`/item/${itemId}`}
			className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.06] transition-colors max-w-[82%]"
		>
			{body}
		</Link>
	);
};

export default ItemPin;
